import { Box, Flex, Icon, Image, Stack, Text } from "@chakra-ui/react";
import { shortenAddress, useEthers } from "@usedapp/core";
import axios from "axios";
import BigNumber from "bignumber.js";
import { format } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IoCalendarClearOutline, IoPersonOutline, IoTimeOutline } from "react-icons/io5";
import { Link } from "react-router-dom";
import { NftResource } from "../../../components/NftResource";
import { colors } from "../../../core/constants/colors";
import { I18N_NAMESPACE } from "../../../core/constants/i18n";
import { useNft } from "../../../core/hooks/useNft";
import { IMoralisNft } from "../../../core/models/nft";
import { ISale } from "../../../core/models/order";
import { getExplorerTxUrl } from "../../../core/utils/getExplorerUrl";
import { uriToHttps } from "../../../core/utils/ipfs";
import { formatNumber } from "../../../core/utils/number";
import { nftService } from "../../../services/nft.service";
import { useAppSelector } from "../../../store/hook";

interface Props {
  sale: ISale;
}

export const SaleItem = React.memo((props: Props) => {
  const { chainId } = useEthers();
  const { t } = useTranslation([I18N_NAMESPACE.PROFILE]);

  const [nft, setNft] = useState<IMoralisNft>();
  const supportedTokens = useAppSelector((state) => state.token.supportedTokens);
  const token = useMemo(
    () => supportedTokens.byId[props.sale.b_token_address],
    [props.sale.b_token_address, supportedTokens]
  );

  useEffect(() => {
    async function getDetail() {
      const nftResponse = await nftService.getNftDetail(
        props.sale.asset_transfer.token_address,
        props.sale.asset_transfer.token_id
      );

      const uri = nftResponse.data.token_uri ? uriToHttps(nftResponse.data.token_uri) : undefined;
      const metadataResponse = uri ? await axios.get(uri) : undefined;

      setNft({
        ...nftResponse.data,
        metadata: metadataResponse?.data,
      });
    }

    getDetail();
  }, []);

  return (
    <Flex
      marginTop={"16px"}
      alignItems={"center"}
      borderWidth={1}
      padding={"12px 20px"}
      borderRadius={"xl"}
      gridGap={"20px"}
    >
      <Box width={"80px"} height={"80px"}>
        <NftResource image={nft?.metadata?.image} animationUrl={nft?.metadata?.animation_url} muted />
      </Box>

      <Box flex={1}>
        <Box fontSize={"small"}>{nft?.name}</Box>
        <Text color={colors.primary2} fontWeight="700" fontSize="20px">
          {nft?.metadata?.name}
        </Text>
      </Box>

      <Stack flex={1}>
        <Flex alignItems={"center"}>
          <Icon as={IoCalendarClearOutline} color={"#a6a6a6"} />
          <Text marginLeft={2} fontSize={"small"} fontWeight={500}>
            {props.sale.created_time && format(props.sale.created_time, "dd/MM/yyyy")}
          </Text>
        </Flex>
        <Flex alignItems={"center"}>
          <Icon as={IoTimeOutline} color={"#a6a6a6"} />
          <Text marginLeft={2} fontSize={"small"} fontWeight={500}>
            {props.sale.created_time && format(props.sale.created_time, "HH:mm")}
          </Text>
        </Flex>
        <Flex alignItems={"center"}>
          <Icon as={IoPersonOutline} color={"#a6a6a6"} />
          <Link to={`/profile/assets/${props.sale.buyer}`}>
            <Text marginLeft={2} fontSize={"small"} fontWeight={700} color={colors.primary} cursor={"pointer"}>
              {shortenAddress(props.sale.buyer)}
            </Text>
          </Link>
        </Flex>
      </Stack>

      <Flex alignItems={"flex-end"} flexDirection={"column"}>
        <Flex alignItems={"center"}>
          <Image src={token?.logoUrl} height="20px" width="20px" marginRight="6px" />
          <Text fontWeight={700} fontSize={"lg"} color={colors.primary1}>
            {formatNumber(new BigNumber(props.sale.price).div(Math.pow(10, token.decimals)).toNumber())}
          </Text>
        </Flex>
        {!!chainId && (
          <a href={getExplorerTxUrl(props.sale.transaction_hash, chainId)} target={"_blank"}>
            <Text fontSize={"small"} fontWeight={500} cursor={"pointer"} color={colors.primary}>
              {t("View on Explorer")}
            </Text>
          </a>
        )}
      </Flex>
    </Flex>
  );
});
